// ===================================================================
// OnboardingModal.tsx — Onboarding nouvelle génération
// Étapes : niveau → objectif → mini-cours (3 mots) → succès
// ===================================================================
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import type { LanguageCode } from '../types/types';
import secureAuthService from '../services/secureAuthService';
import './OnboardingModal.css';

import { EmojiText } from './common/Picto';
export const ONBOARDING_DONE_KEY = 'jogolinga-onboarding-done';

const ONBOARDING_SYNCED_PREFIX = 'jogolinga-onboarding-synced-';

/**
 * À la connexion : envoie au compte le niveau / l'objectif choisis à
 * l'onboarding sur cet appareil, s'ils n'y sont pas encore (onboarding fait
 * en invité, ou par un compte Google avant que l'envoi ne marche pour eux).
 * Une seule fois par compte et par appareil. Ne fait rien si l'onboarding a
 * été passé sans réponse, pour ne pas effacer celles d'un autre appareil.
 */
export async function syncOnboardingToAccount(userId: string): Promise<void> {
  try {
    if (localStorage.getItem(ONBOARDING_DONE_KEY) !== 'true') return;
    if (localStorage.getItem(ONBOARDING_SYNCED_PREFIX + userId) === 'true') return;
    const lang = localStorage.getItem('lastUsedLanguage');
    const level = lang ? localStorage.getItem(`jogolinga-level-${lang}`) : null;
    const objective = lang ? localStorage.getItem(`jogolinga-objective-${lang}`) : null;
    if (!level && !objective) return;
    if (await secureAuthService.completeOnboarding(level, objective)) {
      localStorage.setItem(ONBOARDING_SYNCED_PREFIX + userId, 'true');
    }
  } catch {
    /* silencieux — nouvel essai à la prochaine connexion */
  }
}

// ── Données par langue ──────────────────────────────────────────────

interface FirstWord {
  word: string;
  translation: string;
  translationEn?: string;
  hint?: string;
  hintEn?: string;
}

const FIRST_WORDS: Record<LanguageCode, FirstWord[]> = {
  wf: [
    { word: 'Salamalekum', translation: 'Bonjour',  translationEn: 'Hello',  hint: 'La salutation du Sénégal', hintEn: 'The Senegalese greeting' },
    { word: 'Jërejëf',     translation: 'Merci',    translationEn: 'Thank you', hint: 'Prononce "djé-ré-djef"', hintEn: 'Pronounced "je-re-jef"' },
    { word: 'Waaw',        translation: 'Oui',      translationEn: 'Yes',    hint: 'Prononce "wow"', hintEn: 'Pronounced "wow"' },
  ],
  ba: [
    { word: 'I ni cé',    translation: 'Bonjour',                  translationEn: 'Hello',            hint: 'La salutation mandingue', hintEn: 'The Mandingo greeting' },
    { word: 'I ni baara', translation: 'Merci pour ton travail',   translationEn: 'Thank you for your work', hint: '"Merci" au Mali', hintEn: '"Thank you" in Mali' },
    { word: 'Awa',        translation: 'Oui / D\'accord',          translationEn: 'Yes / Okay',        hint: 'Prononce "awa"', hintEn: 'Pronounced "awa"' },
  ],
  la: [
    { word: 'Mbote',  translation: 'Bonjour',           translationEn: 'Hello',        hint: 'La salutation de Kinshasa', hintEn: 'The Kinshasa greeting' },
    { word: 'Malamu', translation: 'Bien / Bon',         translationEn: 'Good / Fine',   hint: 'Prononce "ma-la-mou"', hintEn: 'Pronounced "ma-la-mu"' },
    { word: 'Ndéko',  translation: 'Ami / Camarade',    translationEn: 'Friend / Comrade', hint: 'Un mot très utilisé à Kin', hintEn: 'A word widely used in Kinshasa' },
  ],
  ff: [
    { word: 'Jam waali',  translation: 'Bonjour',   translationEn: 'Hello', hint: 'La paix soit sur ta journée', hintEn: 'Peace be upon your day' },
    { word: 'A jaraama',  translation: 'Merci',     translationEn: 'Thank you', hint: 'Prononce "a dja-ra-ma"', hintEn: 'Pronounced "a ja-ra-ma"' },
    { word: 'Eey',        translation: 'Oui',       translationEn: 'Yes',   hint: 'Simple et court !', hintEn: 'Simple and short!' },
  ],
  co: [
    { word: 'Shahri',  translation: 'Bonjour',            translationEn: 'Hello',        hint: 'Salutation copte ancienne', hintEn: 'Ancient Coptic greeting' },
    { word: 'Agapos',  translation: 'Amour',              translationEn: 'Love',          hint: 'Mot fondateur du copte chrétien', hintEn: 'A foundational word of Christian Coptic' },
    { word: 'Amen',    translation: 'Ainsi soit-il',      translationEn: 'So be it',      hint: 'Universel depuis l\'Égypte antique', hintEn: 'Universal since ancient Egypt' },
  ],
  sw: [
    { word: 'Habari',  translation: 'Bonjour / Quoi de neuf ?', translationEn: 'Hello / What\'s new?', hint: 'Réponse : "Nzuri" (bien)', hintEn: 'Reply: "Nzuri" (good)' },
    { word: 'Asante',  translation: 'Merci',                    translationEn: 'Thank you',            hint: 'Prononce "a-san-té"', hintEn: 'Pronounced "a-san-teh"' },
    { word: 'Karibu',  translation: 'Bienvenue',                translationEn: 'Welcome',              hint: 'Utilisé partout en Afrique de l\'Est', hintEn: 'Used all over East Africa' },
  ],
  ka: [
    { word: 'ꜥnḫ', translation: 'Vie',                 translationEn: 'Life',                  hint: 'Le symbole ankh, la croix à anse', hintEn: 'The ankh symbol, the looped cross' },
    { word: 'nfr',  translation: 'Beau, bon, parfait',  translationEn: 'Beautiful, good, perfect', hint: 'Un des signes les plus fréquents des hiéroglyphes', hintEn: 'One of the most frequent hieroglyphic signs' },
    { word: 'nṯr',  translation: 'Dieu, divin',         translationEn: 'God, divine',            hint: 'Le drapeau sur un mât', hintEn: 'The flag on a mast' },
  ],
  yo: [
    { word: 'Báwo ni?', translation: 'Comment ça va ?', translationEn: 'How are you?', hint: 'La salutation la plus courante', hintEn: 'The most common greeting' },
    { word: 'Ẹ ṣé',     translation: 'Merci',          translationEn: 'Thank you',    hint: 'Prononce "è ché"', hintEn: 'Pronounced "eh sheh"' },
    { word: 'Bẹ́ẹ̀ ni',  translation: 'Oui',            translationEn: 'Yes',          hint: 'Les tons comptent : haut puis bas', hintEn: 'Tones matter: high then low' },
  ],
};

const LANG_NAMES: Record<LanguageCode, string> = {
  wf: 'Wolof', ba: 'Bambara', la: 'Lingala', ff: 'Peul', co: 'Copte', sw: 'Swahili', ka: 'Kama', yo: 'Yoruba',
};

const LANG_FLAGS: Record<LanguageCode, string> = {
  wf: '🇸🇳', ba: '🇲🇱', la: '🇨🇩', ff: '🇬🇳', co: '🇪🇬', sw: '🇹🇿', ka: '𓋹', yo: '🇳🇬',
};

// ── Types internes ──────────────────────────────────────────────────

type Step = 'level' | 'objective' | 'lesson' | 'success';

interface ChoiceOption {
  id: string;
  label: string;
  emoji: string;
}

// ── Mascotte inline ─────────────────────────────────────────────────

const ObMascot: React.FC<{ message: string }> = ({ message }) => (
  <div className="ob-mascot-row">
    <motion.img
      src="/Mascotte/mascotte5.png"
      alt="Mascotte"
      className="ob-mascot-img"
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      key={message}
      className="ob-mascot-bubble"
      initial={{ opacity: 0, scale: 0.88, x: -8 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      transition={{ duration: 0.22 }}
    >
      <EmojiText text={message} />
    </motion.div>
  </div>
);

// ── Composant principal ─────────────────────────────────────────────

interface Props {
  onClose: () => void;
  languageCode: LanguageCode;
  /** Lance directement la 1re leçon après le mini-cours (pas sur "Passer"). */
  onStartFirstLesson?: () => void;
}

const STEP_ORDER: Step[] = ['level', 'objective', 'lesson', 'success'];

const OnboardingModal: React.FC<Props> = ({ onClose, languageCode, onStartFirstLesson }) => {
  const { t, i18n } = useTranslation();
  const LEVELS: ChoiceOption[] = [
    { id: 'zero',   label: t('onboarding.levels.zero'),   emoji: '🌱' },
    { id: 'some',   label: t('onboarding.levels.some'),   emoji: '🌿' },
    { id: 'medium', label: t('onboarding.levels.medium'), emoji: '🌳' },
  ];
  const OBJECTIVES: ChoiceOption[] = [
    { id: 'family',  label: t('onboarding.objectives.family'),  emoji: '👨‍👩‍👧' },
    { id: 'travel',  label: t('onboarding.objectives.travel'),  emoji: '✈️'   },
    { id: 'culture', label: t('onboarding.objectives.culture'), emoji: '🎭'   },
  ];
  const [step, setStep]           = useState<Step>('level');
  const [dir, setDir]             = useState(1);
  const [levelId, setLevelId]     = useState<string | null>(null);
  const [objectiveId, setObjectiveId] = useState<string | null>(null);
  const [lessonIdx, setLessonIdx] = useState(0);
  const [revealed, setRevealed]   = useState(false);

  const words    = FIRST_WORDS[languageCode] ?? FIRST_WORDS.wf;
  const langName = LANG_NAMES[languageCode]  ?? 'Wolof';
  const langFlag = LANG_FLAGS[languageCode]  ?? '🌍';

  const goTo = (next: Step) => {
    const curIdx  = STEP_ORDER.indexOf(step);
    const nextIdx = STEP_ORDER.indexOf(next);
    setDir(nextIdx > curIdx ? 1 : -1);
    setStep(next);
  };

  const finish = () => {
    localStorage.setItem(ONBOARDING_DONE_KEY, 'true');
    if (levelId)     localStorage.setItem(`jogolinga-level-${languageCode}`, levelId);
    if (objectiveId) localStorage.setItem(`jogolinga-objective-${languageCode}`, objectiveId);

    // XP uniquement si le mini-cours a été complété (pas sur "Passer")
    if (step === 'success') {
      const XP_REWARD = 15;
      const currentXP = parseInt(localStorage.getItem(`${languageCode}-totalXP`) || '0');
      const newTotalXP = currentXP + XP_REWARD;
      localStorage.setItem(`${languageCode}-totalXP`, newTotalXP.toString());
      window.dispatchEvent(new CustomEvent('xpUpdated', {
        detail: { newTotal: newTotalXP, gained: XP_REWARD, source: 'onboarding' }
      }));
    }

    // Persister en base si l'utilisateur est connecté (cross-browser)
    secureAuthService.completeOnboarding(levelId, objectiveId);
    onClose();
    if (step === 'success') onStartFirstLesson?.();
  };

  const handleReveal = () => setRevealed(true);

  const handleNextWord = () => {
    if (lessonIdx < words.length - 1) {
      setLessonIdx(i => i + 1);
      setRevealed(false);
    } else {
      goTo('success');
    }
  };

  // ── Messages mascotte ─────────────────────────────────────────────

  const mascotMessage = (() => {
    if (step === 'level')     return t('onboarding.mascot.level');
    if (step === 'objective') return t('onboarding.mascot.objective');
    if (step === 'lesson')    return revealed ? t('onboarding.mascot.lessonRevealed') : t('onboarding.mascot.lessonHidden');
    return t('onboarding.mascot.success', { lang: langName });
  })();

  // ── Dot de progression ────────────────────────────────────────────

  const dotIndex = STEP_ORDER.indexOf(step);

  return (
    <div className="ob-overlay">
      <div className="ob-panel">

        {/* Dots de progression */}
        <div className="ob-dots">
          {STEP_ORDER.map((_, i) => (
            <div
              key={i}
              className={`ob-dot${i === dotIndex ? ' ob-dot--active' : i < dotIndex ? ' ob-dot--done' : ''}`}
            />
          ))}
        </div>

        {/* Mascotte */}
        <ObMascot message={mascotMessage} />

        {/* Contenu de l'étape */}
          <motion.div
            key={step + lessonIdx}
            className="ob-step"
            initial={{ x: dir * 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >

            {/* ── Étape 1 : Niveau ─────────────────────────────── */}
            {step === 'level' && (
              <>
                <h2 className="ob-title">
                  {t('onboarding.levelTitle', { lang: langName, flag: langFlag })}
                </h2>
                <div className="ob-options">
                  {LEVELS.map(opt => (
                    <button
                      key={opt.id}
                      className={`ob-option-btn${levelId === opt.id ? ' ob-option-btn--active' : ''}`}
                      onClick={() => {
                        setLevelId(opt.id);
                        setTimeout(() => goTo('objective'), 180);
                      }}
                    >
                      <span className="ob-option-emoji"><EmojiText text={opt.emoji} /></span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ── Étape 2 : Objectif ───────────────────────────── */}
            {step === 'objective' && (
              <>
                <h2 className="ob-title">
                  {t('onboarding.objectiveTitle', { lang: langName })}
                </h2>
                <div className="ob-options">
                  {OBJECTIVES.map(opt => (
                    <button
                      key={opt.id}
                      className={`ob-option-btn${objectiveId === opt.id ? ' ob-option-btn--active' : ''}`}
                      onClick={() => {
                        setObjectiveId(opt.id);
                        setTimeout(() => goTo('lesson'), 180);
                      }}
                    >
                      <span className="ob-option-emoji"><EmojiText text={opt.emoji} /></span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ── Étape 3 : Mini-cours ─────────────────────────── */}
            {step === 'lesson' && (
              <>
                <div className="ob-lesson-header">
                  <h2 className="ob-title">
                    {t('onboarding.wordCounter', { current: lessonIdx + 1, total: words.length })}
                  </h2>
                  <div className="ob-lesson-pips">
                    {words.map((_, i) => (
                      <div
                        key={i}
                        className={`ob-lesson-pip${i <= lessonIdx ? ' ob-lesson-pip--done' : ''}`}
                      />
                    ))}
                  </div>
                </div>

                <div
                  className={`ob-flashcard${revealed ? ' ob-flashcard--revealed' : ''}`}
                  onClick={!revealed ? handleReveal : undefined}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' && !revealed) handleReveal(); }}
                  aria-label={revealed ? undefined : (t('onboarding.revealAria') as string)}
                >
                  <div className="ob-flashcard-inner">
                    <div className="ob-flashcard-front">
                      <span className="ob-flashcard-word">{words[lessonIdx].word}</span>
                      <span className="ob-flashcard-tap"><EmojiText text={t('onboarding.tapToReveal') as string} /></span>
                    </div>
                    <div className="ob-flashcard-back">
                      <span className="ob-flashcard-translation">
                        {i18n.language === 'en' && words[lessonIdx].translationEn
                          ? words[lessonIdx].translationEn
                          : words[lessonIdx].translation}
                      </span>
                      {words[lessonIdx].hint && (
                        <span className="ob-flashcard-hint">
                          {i18n.language === 'en' && words[lessonIdx].hintEn
                            ? words[lessonIdx].hintEn
                            : words[lessonIdx].hint}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {revealed && (
                  <motion.button
                    className="ob-btn ob-btn--primary ob-btn--full"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={handleNextWord}
                  >
                    {lessonIdx < words.length - 1 ? t('onboarding.nextWord') : t('onboarding.seeResult')}
                  </motion.button>
                )}
              </>
            )}

            {/* ── Étape 4 : Succès ─────────────────────────────── */}
            {step === 'success' && (
              <div className="ob-success">
                <motion.div
                  className="ob-success-xp"
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.35, 1] }}
                  transition={{ duration: 0.5, times: [0, 0.6, 1] }}
                >
                  <EmojiText text={t('onboarding.xpEarned') as string} />
                </motion.div>
                <h2 className="ob-title">
                  {t('onboarding.successTitle', { count: words.length, lang: langName })}
                </h2>
                <p className="ob-body">
                  <EmojiText text={t('onboarding.successBody') as string} />
                </p>
                <button
                  className="ob-btn ob-btn--primary ob-btn--full"
                  style={{ marginTop: 8 }}
                  onClick={finish}
                >
                  {onStartFirstLesson
                    ? t('onboarding.startFirstLesson', { flag: langFlag })
                    : t('onboarding.startAdventure', { flag: langFlag })}
                </button>
              </div>
            )}

          </motion.div>

        {/* Passer — uniquement sur les étapes info */}
        {(step === 'level' || step === 'objective') && (
          <button className="ob-skip" onClick={finish}>
            {t('onboarding.skip')}
          </button>
        )}

      </div>
    </div>
  );
};

export default OnboardingModal;
